import { EMPTY, Observable, from } from 'rxjs';
import {
  FormPagerQueryModel,
  FormTemplate,
  FormTemplateStatus,
  SearchLibraryTemplatesParams,
  SearchModel,
  SearchType,
} from '@nrwl/rx-models';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { FormConfig } from '@nrwl/rx-form-builder';

export enum UploadStatus {
  Uploading = 1,
  Uploaded = 2,
  Failed = 3,
}

export class UploadPaidTemplateModel {
  id: number;
  name: string;
  data: string;
  status: number;
}

@Injectable({
  providedIn: 'root',
})
export class FormTemplateService {
  public filesToUpload: UploadPaidTemplateModel[] = [];
  public currentIndex: number;
  public formData: any;
  public uploadStatus = UploadStatus;
  public formTemplateUrl = environment.baseAPIUrlv3 + `formtemplate/`;

  constructor(private http: HttpClient) {}

  private buildFormTemplatePayload(formTemplate: FormTemplate): any {
    const formData: any = { ...formTemplate };
    formData.id = void 0;

    if (formData.category && typeof formData.category === 'object') {
      formData.category = formData.category.id;
    }

    return formData;
  }

  getHeaders(): HttpHeaders {
    const headers = new HttpHeaders().set('page_size', '20');
    return headers;
  }

  // public getFormTemplate(templateID): Observable<FormTemplate> {
  // 	return this.http.get<FormTemplate>(
  // 		environment.baseAPIUrl + `formtemplate/${templateID}/`
  // 	);
  // }

  public getSelectedFormTemplateById(action): Observable<any> {
    // RWE-2527: FORMS v2 to v3
    return this.http.get(environment.baseAPIUrlv3 + `formtemplate/${action.payload}/`);
  }

  public getArchiveTemplates(): Observable<any> {
    // RWE-2527: FORMS v2 to v3
    return this.http
      .get(environment.baseAPIUrlv3 + `formtemplate/?is_deleted=true&is_stock=both`)
      .pipe(
        map((res: any) => {
          return res.map((template) => new FormTemplate(template));
        }),
      );
  }

  public getPracticeTemplates(clinicId): Observable<FormTemplate[]> {
    // RWE-2527: FORMS v2 to v3
    const url =
      environment.baseAPIUrlv3 +
      `formtemplate/?is_deleted=false&clinic=${clinicId}&uncategorized=true&is_stock=both`;
    return this.http.get(url).pipe(
      map((res: any) => {
        return res.map((template) => new FormTemplate(template));
      }),
    );
  }

  public getPracticeArchive(clinicId): Observable<FormTemplate[]> {
    // RWE-2527: FORMS v2 to v3
    const url =
      environment.baseAPIUrlv3 +
      `formtemplate/?is_deleted=true&clinic=${clinicId}&uncategorized=true`;
    return this.http.get(url).pipe(
      map((res: any) => {
        return res.map((template) => new FormTemplate(template));
      }),
    );
  }

  public getUploadedTemplates(): Observable<FormTemplate[]> {
    // RWE-2527: FORMS v2 to v3
    const url = environment.baseAPIUrlv3 + `formtemplate/?is_deleted=false`;
    return this.http.get(url).pipe(
      map((res: any) => {
        return res.map((template) => new FormTemplate(template));
      }),
    );
  }

  public getAllFormTemplates(): Observable<FormTemplate[]> {
    const url = this.formTemplateUrl;

    return this.http.get<FormTemplate[]>(url).pipe(
      map((res: any) => {
        return res.map((template) => new FormTemplate(template));
      }),
    );
  }

  public getFormTemplates(pager: FormPagerQueryModel): Observable<FormTemplate[]> {
    let url: string = this.formTemplateUrl;
    const category: number = pager.categoryID;
    const page = pager.page || 1;
    const page_size = pager.page_size || 1000;
    const query: string[] = [];

    query.push(`category=${category}`);
    query.push(`is_stock=${pager.searchType === SearchType.GlobalLibrary}`);
    query.push(`page=${page}`);
    query.push(`page_size=${page_size}`);

    if (pager.type) {
      query.push(`type=${pager.type}`);
    }

    if (pager.is_pne !== undefined) {
      query.push(`is_pne=${pager.is_pne}`);
    }

    url = `${url}?${query.join('&')}`;

    return this.http.get<FormTemplate[]>(url).pipe(
      map((res: any) => {
        return res.map((template) => new FormTemplate(template));
      }),
    );
  }

  public getFormTemplatesAccountLibrary(patientId: number): Observable<FormTemplate[]> {
    const url =
      environment.baseAPIUrlv3 +
      `form-template/?is_deleted=false&is_stock=false&omit=clinic,parent,files,attachment,signees,media,main_text,validity_period,status,is_stock,is_deleted&expand=category&patient_id=${patientId}&category_is_stock=false&category_is_deleted=false`;

    return this.http.get<FormTemplate[]>(url).pipe(
      map((res: any) => {
        return res.map((template) => new FormTemplate(template));
      }),
    );
  }

  public getFormTemplatesAccountLibraryEx(
    patientId: number,
    type?: string,
  ): Observable<FormTemplate[]> {
    let params = new HttpParams()
      .set('category_is_deleted', 'false')
      .set('category_is_stock', 'false')
      .set('is_stock', 'false')
      .set('is_deleted', 'false')
      .set('expand', 'category')
      .set('patient_id', String(patientId))
      .set(
        'fields',
        'id,name,order,completed_at,category.id,category.name,category.order,is_pne,type',
      );

    if (type) {
      params = params.set('type', type);
    }

    return this.http
      .get<FormTemplate[]>(environment.baseAPIUrlv3 + 'form-template/', { params })
      .pipe(
        map((res: any) => {
          return res.map((template) => new FormTemplate(template));
        }),
      );
  }

  public getFormTemplatesByName(searchModel: SearchModel): Observable<FormTemplate[]> {
    let url: string = environment.baseAPIUrlv3 + `formtemplate/`;
    const query: string[] = [];

    query.push(`search=${searchModel.keyword}`);
    query.push(`expand=category`);

    if (
      searchModel.type === SearchType.GlobalLibraryArchive ||
      searchModel.type === SearchType.AccountLibraryArchive ||
      searchModel.type === SearchType.AccountLibraryUploadedArchived ||
      searchModel.type === SearchType.PracticeLibraryPendingArchived ||
      searchModel.type === SearchType.PracticeLibraryPublishedArchived
    ) {
      query.push(`is_deleted=true`);
    }

    if (
      searchModel.type === SearchType.PracticeLibraryPending ||
      searchModel.type === SearchType.PracticeLibraryPublished ||
      searchModel.type === SearchType.PracticeLibraryPendingArchived ||
      searchModel.type === SearchType.PracticeLibraryPublishedArchived
    ) {
      query.push(`uncategorized=true`);
      query.push(`is_stock=both`);
      query.push(`clinic=${searchModel.clinic}`);
    } else {
      const isGlobalLibrary =
        searchModel.type === SearchType.GlobalLibrary ||
        searchModel.type === SearchType.GlobalLibraryArchive;
      query.push(`is_stock=${isGlobalLibrary}`);
    }

    if (searchModel.is_pne !== undefined) {
      query.push(`is_pne=${searchModel.is_pne}`);
    }

    url = `${url}?${query.join('&')}`;

    return this.http.get<FormTemplate[]>(url).pipe(
      map((res: any) => {
        return res.map((template) => {
          const categoryData = template.category;
          const t = new FormTemplate({
            ...template,
            category: typeof categoryData === 'object' ? categoryData?.id : categoryData,
          });
          (t as any).category_name = typeof categoryData === 'object' ? categoryData?.name : null;
          return t;
        });
      }),
    );
  }

  public searchLibraryTemplates(
    params: SearchLibraryTemplatesParams = {},
  ): Observable<FormTemplate[]> {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        if (value.length) {
          httpParams = httpParams.set(key, value.join(','));
        }
        return;
      }

      httpParams = httpParams.set(key, String(value));
    });

    return this.http
      .get<FormTemplate[]>(environment.baseAPIUrlv3 + 'formtemplate/', {
        params: httpParams,
      })
      .pipe(
        map((res: any) => {
          return res.map((template) => {
            const categoryData = template.category;
            const model = new FormTemplate({
              ...template,
              category: typeof categoryData === 'object' ? categoryData?.id : categoryData,
            });
            (model as any).category_name =
              typeof categoryData === 'object' ? categoryData?.name : null;
            return model;
          });
        }),
      );
  }

  public getFormTemplateById(formId: number): Observable<FormTemplate> {
    return this.http
      .get<FormTemplate[]>(environment.baseAPIUrlv3 + `formtemplate/${formId}/`)
      .pipe(map((t) => new FormTemplate(t)));
  }

  public getExpandedFormTemplateByID(formId: number, patientId: number): Observable<FormTemplate> {
    return this.http
      .get<
        FormTemplate[]
      >(environment.baseAPIUrlv3 + `formtemplate/${formId}/?patient_id=${patientId}`)
      .pipe(map((t) => new FormTemplate(t)));
  }

  public setStatus(formId: number, status: FormTemplateStatus) {
    // RWE-2527: FORMS v2 to v3
    this.http
      .get<FormTemplate[]>(environment.baseAPIUrlv3 + `formtemplate/?fields=id,is_stock,status`)
      .subscribe((q) => {});

    return this.http.patch(environment.baseAPIUrl + `formtemplate/${formId}/`, {
      status: <number>status,
    });
  }

  public save(formId, formTemplate: FormTemplate): Observable<any> {
    const formData = this.buildFormTemplatePayload(formTemplate);
    return this.http
      .patch(environment.baseAPIUrlv3 + `formtemplate/${formId}/`, formData)
      .pipe(map((tmpl) => new FormTemplate(tmpl)));
  }

  public patch(formTemplateId: number, params: any) {
    return this.http
      .patch(environment.baseAPIUrlv3 + `formtemplate/${formTemplateId}/`, params)
      .pipe(map((tmpl) => new FormTemplate(tmpl)));
  }

  public create(formTemplate: FormTemplate): Observable<any> {
    const formData = this.buildFormTemplatePayload(formTemplate);
    return this.http
      .post(environment.baseAPIUrlv3 + `formtemplate/`, formData)
      .pipe(map((tmpl) => new FormTemplate(tmpl)));
  }

  public loadByIdAndCreate(
    templateId: number,
    categoryId: number,
    name: any = null,
  ): Observable<any> {
    // WARNING: HACK: Preload template field and then create.
    // IMPROVE ONCE RWE-1060 is done.
    return from(
      new Promise((resolve, reject) => {
        this.getFormTemplateById(templateId)
          .pipe(take(1))
          .subscribe((tmplFull) => {
            const clone = new FormTemplate(tmplFull);
            clone.category = categoryId;
            clone.parent = tmplFull.id;
            clone.name = name ? name : clone.name;
            this.create(clone)
              .pipe(take(1))
              .subscribe((tmpl) => {
                resolve(new FormTemplate(tmpl));
              });
          });
      }),
    );
  }

  public delete(formId: number): Observable<any> {
    // RWE-2527: FORMS v2 to v3
    return this.http.delete(environment.baseAPIUrlv3 + `formtemplate/${formId}/`);
  }

  public restore(formTemplate: FormTemplate | any): Observable<any> {
    let name = formTemplate.name?.replace(' (Restored)', '');
    name += ' (Restored)';

    // RWE-2527: FORMS v2 to v3
    return this.http
      .patch(environment.baseAPIUrlv3 + `formtemplate/${formTemplate.id}/`, {
        name: name,
        is_deleted: false,
        category: formTemplate.category || void 0,
      })
      .pipe(map((tmpl) => new FormTemplate(tmpl)));
  }

  public upload(filesToUpload?: any): any {
    if (filesToUpload) {
      this.filesToUpload = filesToUpload;
    }

    // Anything to upload?
    if (this.filesToUpload !== undefined) {
      this.currentIndex = this.filesToUpload.findIndex((file) => file.status < 1);
    }

    if (this.currentIndex < 0 || this.currentIndex === undefined) {
      return;
    }

    this.filesToUpload[this.currentIndex].status = this.uploadStatus.Uploading;

    this.formData = new FormConfig();

    const tpl = new FormTemplate({
      name: this.filesToUpload[this.currentIndex].name,
      category: null,
      template: JSON.stringify(this.formData),
    });

    // 1 CreateForm
    // RWE-2527: FORMS v2 to v3
    return this.http
      .post(environment.baseAPIUrlv3 + `formtemplate/`, tpl)
      .pipe(
        switchMap((template: FormTemplate | any) => {
          // 2 Attach file to created form

          const formData = new FormData();
          formData.append('content', this.filesToUpload[this.currentIndex].data);
          formData.append('type', '1');
          formData.append('template', String(template.id));

          // RWE-2527: FORMS v2 to v3
          return this.http.post(`${environment.baseAPIUrlv3}formtemplatefiles/`, formData);
        }),
        catchError((err: any) => {
          this.filesToUpload[this.currentIndex].status = this.uploadStatus.Failed;
          return EMPTY;
        }),
      )
      .subscribe((result) => {
        // 3 Change uploaded file status
        this.filesToUpload[this.currentIndex].status = this.uploadStatus.Uploaded;

        // 4. Check if there more files to upload
        if (this.filesToUpload.findIndex((file) => file.status === 0)) {
          this.upload();
        }
      });
  }
}
